/* AgentSpec.ts */

/**
 * Wire contract of the insights-agent spec endpoint.
 *
 * The agent answers a prompt with a *configuration spec* — which
 * analysis to show and which filter values to set — rather than
 * with data. The frontend applies the spec to its own controls and
 * queries the insights backend itself, so no patient data is ever
 * sent to an LLM.
 *
 * Every filter field is optional and may come back as `null`: a
 * spec is a **patch** over the current controls. Both `null` and
 * absent mean "leave this control unchanged".
 *
 * TODO(phase 3.1): replace these hand-written types with types
 * generated from the agent's OpenAPI schema. The `spec_version`
 * guard already lives in `requestAgentSpec`.
 */

/* Interfaces */
import { InsightsFilterProps } from "src/shared/interfaces/insights-filter/InsightsFilter";

/* Version of the contract this frontend understands. */
export const AGENT_SPEC_VERSION = 1;

/* A spec request must not hang as long as a chat answer may. */
export const AGENT_SPEC_TIMEOUT_MS = 30000;

export enum AgentAnalysis {
  DESCRIPTIVE_STATISTICS = "descriptive_statistics",
  SURVIVAL_ANALYSIS = "survival_analysis",
  TREATMENT_PATTERNS = "treatment_patterns",
}

interface AgentSpecBase {
  main_condition?: string | null;
  cohort?: string | null;
  first_year?: number | null;
  last_year?: number | null;
  care_sites?: string[] | null;
}

export interface DescriptiveStatisticsSpec
  extends AgentSpecBase {
  analysis: AgentAnalysis.DESCRIPTIVE_STATISTICS;
  feature?: string | null;
}

export interface SurvivalAnalysisSpec
  extends AgentSpecBase {
  analysis: AgentAnalysis.SURVIVAL_ANALYSIS;
  start_point?: string | null;
}

export interface TreatmentPatternsSpec
  extends AgentSpecBase {
  analysis: AgentAnalysis.TREATMENT_PATTERNS;
  custom_feature?: string | null;
  max_depth?: number | null;
}

/**
 * Discriminated on `analysis`: the three analyses take genuinely
 * different parameters, so a survival spec cannot carry a feature.
 */
export type AgentSpec =
  | DescriptiveStatisticsSpec
  | SurvivalAnalysisSpec
  | TreatmentPatternsSpec;

/**
 * Something the user asked for that could not be honoured, e.g. a
 * year outside the deployment's data. Shown to the user rather
 * than dropped silently.
 */
export interface AgentUnresolvedField {
  field: string;
  requested: string;
  reason: string;
}

export type AgentSpecStatus =
  | "idle"
  | "loading"
  | "success"
  | "error";

/**
 * The controls as they were before a spec was applied, so the user
 * can undo it. Covers the other slices a spec can touch too — a
 * half-undo would be worse than none.
 */
export interface AgentSpecSnapshot {
  insightsFilter: InsightsFilterProps;
  startPoint?: string;
  maxDepth: number;
  /**
   * The route the user was on before the spec was applied. A spec that
   * changes the analysis also navigates, so restoring only the controls
   * would leave the user on the new page — not the state they had.
   */
  pathname: string;
}

export interface AgentSpecResponse {
  spec_version: number;
  spec: AgentSpec | null;
  summary: string;
  clarification: string | null;
  unresolved: AgentUnresolvedField[];
}

/**
 * The controls as they currently stand, sent with the prompt.
 *
 * Serves two purposes: it lets the agent patch instead of replace,
 * and its option lists are the only values a spec may choose from
 * — which is what keeps a generated spec valid for the deployed
 * data.
 */
/**
 * The languages the assistant will ask a question in.
 *
 * A choice rather than something worked out from the prompt. Inferring
 * it was tried twice and both attempts were worse than not trying:
 * word lists did not generalise past the prompts they were built from,
 * and the browser locale is always present, so a German one turned
 * three English words into a German question every time.
 */
/**
 * The picker entry meaning "send no model, let the deployment choose".
 *
 * It exists so the picker can be added without changing what a normal
 * user gets. `/models/allowed` returns `test` first, so selecting the
 * first allowed model by default would silently move everyone onto the
 * keyword generator instead of the configured provider model.
 */
export const AGENT_DEFAULT_MODEL_LABEL = "Deployment default";

/**
 * The agent's built-in keyword matcher. Named here because the panel
 * has to tell the user something different about it: it runs inside the
 * deployment and sends nothing to a provider.
 */
export const AGENT_LOCAL_MODEL = "test";

export const AGENT_ANSWER_LANGUAGES = ["en", "de"] as const;

export type AgentAnswerLanguage =
  (typeof AGENT_ANSWER_LANGUAGES)[number];

/* English, because that is the direction that kept breaking. */
export const DEFAULT_AGENT_ANSWER_LANGUAGE: AgentAnswerLanguage =
  "en";

export const AGENT_ANSWER_LANGUAGE_LABELS: Record<
  AgentAnswerLanguage,
  string
> = { en: "English", de: "Deutsch" };

export interface AgentCurrentState {
  analysis: AgentAnalysis | null;
  main_condition?: string;
  cohort?: string;
  feature?: string;
  custom_feature?: string;
  /* The analysis-specific selections: without these the agent is not told
     they are already chosen, and asks about them on every prompt. */
  start_point?: string;
  max_depth?: number;
  first_year?: number;
  last_year?: number;
  care_sites: string[];
  available_main_conditions: string[];
  available_features: string[];
  available_cohorts: string[];
  available_custom_features: string[];
  available_start_points: string[];
  available_care_sites: string[];
  available_first_year?: number;
  available_last_year?: number;
}
